import React from 'react'
import { Money } from '@core/Components/Text/Money'
import { NoticeBox } from '@core/Components/NoticeBox'
import type { BetSpecialOffer, EventDetails } from '@mobi/betslip/types'
import { useTrackOptimoveEvent } from '@core/Services/Optimove/hooks'
import type { QuickbetReceiptState } from './driver'
import {
  ReceiptListItemTextStyled,
  ReceiptListItemStyled,
  ReceiptListStyled,
  ReceiptCampaignTextStyled,
  ReceiptListRowStyled,
} from './Receipt.styles'
import { RaceAlertButton } from './Components/RaceAlertButton'
import { ShareButton } from './Components/ShareButton'
import {
  RecieptButtonsContainerStyled,
  RecieptContainerStyled,
} from './Components/Common/Receipt.styles'
import { setQualtricsIntercept } from '@core/Areas/Qualtrics/qualtrics'
import { Icon } from '@mobi/component-library/Common/Icon'
import { hexColors } from '@mobi/settings/hexColors'

export type QuickbetReceiptProps = {
  isToteBet: boolean
  isMysteryBet: boolean
  isMysteryCombo: boolean
  isNoveltyBet: boolean
  itemSelectionTag?: string
  showBalance: boolean
  selectionDetails: EventDetails | null
} & Pick<
  Immutable<QuickbetReceiptState>,
  | 'accountBalance'
  | 'ticketNumber'
  | 'betCost'
  | 'winInvestment'
  | 'placeInvestment'
  | 'projectedWinPayout'
  | 'projectedPlacePayout'
  | 'specialOffers'
  | 'hasSpecialOffer'
  | 'divXAnnotations'
  | 'campaignActivatedInd'
  | 'campaignType'
  | 'bonusBet'
  | 'amountPaid'
>

export const QuickbetReceipt: React.FC<QuickbetReceiptProps> = ({ ...props }) => {
  useTrackOptimoveEvent({ eventName: 'bet_placed', data: { type: 'quick_bet' } })

  const { selectionDetails, isMysteryBet, isMysteryCombo } = props

  const shouldAllowShare = !isMysteryBet || !isMysteryCombo

  const specialOffers = props.specialOffers as BetSpecialOffer[]

  setQualtricsIntercept(specialOffers.length > 0 ? 'special' : props.bonusBet ? 'bonus' : 'bet')

  return (
    <RecieptContainerStyled>
      <NoticeBox noticeBoxType='Success' title='Your bet has been placed.' paddingOverride='0'>
        <QuickbetReceiptDetails {...props} />
      </NoticeBox>

      <RecieptButtonsContainerStyled>
        {shouldAllowShare && (
          <ShareButton
            winInvestment={props.winInvestment}
            placeInvestment={props.placeInvestment}
            bonusBet={props.bonusBet}
            ticketNumber={props.ticketNumber}
            betCost={props.betCost}
            specialOffers={specialOffers}
            hasSpecialOffer={props.hasSpecialOffer ?? false}
          />
        )}

        <RaceAlertButton selectionDetails={selectionDetails} />
      </RecieptButtonsContainerStyled>
    </RecieptContainerStyled>
  )
}

export const QuickbetReceiptDetails: React.FC<
  Omit<QuickbetReceiptProps, 'selectionDetails' | 'hasSpecialOffer'>
> = ({
  isToteBet,
  isMysteryBet,
  isMysteryCombo,
  isNoveltyBet,
  accountBalance,
  ticketNumber,
  betCost,
  winInvestment,
  placeInvestment,
  projectedWinPayout,
  projectedPlacePayout,
  specialOffers,
  divXAnnotations,
  campaignActivatedInd,
  campaignType,
  bonusBet = 0,
  amountPaid,
  showBalance,
  itemSelectionTag,
}) => {
  const derivedBetCost: number =
    isToteBet || isMysteryBet ? (betCost ?? 0) : (winInvestment ?? 0) + (placeInvestment ?? 0)
  const shouldDisplayFlexi = (isMysteryBet && !isMysteryCombo) || (isToteBet && isNoveltyBet)
  const projectedPay = (projectedWinPayout ?? 0) + (projectedPlacePayout ?? 0)
  const betCostMessage =
    itemSelectionTag && itemSelectionTag !== '' ? `Bet Cost (${itemSelectionTag})` : 'Bet Cost'
  const wasCampaignActivated = campaignActivatedInd && !!bonusBet
  const isBonusBet = campaignType == 'BonusBetReward'

  return (
    <ReceiptListStyled>
      {shouldDisplayFlexi && (
        <ReceiptListRowStyled data-tid-receipt-flexi=''>
          <ReceiptListItemStyled>Flexi</ReceiptListItemStyled>
          <ReceiptListItemTextStyled>{divXAnnotations}</ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      )}

      {!(isToteBet || isMysteryBet) && projectedPay > 0 && (
        <ReceiptListRowStyled data-tid-receipt-projrtn=''>
          <ReceiptListItemStyled>Projected Return</ReceiptListItemStyled>
          <ReceiptListItemTextStyled>
            <Money amount={projectedPay} />
          </ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      )}

      <ReceiptListRowStyled data-tid-receipt-betcost=''>
        <ReceiptListItemStyled>{betCostMessage}</ReceiptListItemStyled>
        <ReceiptListItemTextStyled>
          <Money amount={derivedBetCost} />
        </ReceiptListItemTextStyled>
      </ReceiptListRowStyled>

      {!!wasCampaignActivated && (
        <ReceiptListRowStyled data-tid-receipt-bonusbet=''>
          <ReceiptListItemStyled>
            <ReceiptCampaignTextStyled isBonusBet={isBonusBet}>
              {isBonusBet ? 'Bonus Bet' : 'Bonus Cash'}
            </ReceiptCampaignTextStyled>
          </ReceiptListItemStyled>
          <ReceiptListItemTextStyled>
            <ReceiptCampaignTextStyled isBonusBet={isBonusBet}>
              <Money amount={-(bonusBet ?? 0)} />
            </ReceiptCampaignTextStyled>
          </ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      )}

      {!!wasCampaignActivated && (
        <ReceiptListRowStyled data-tid-receipt-amountpaid=''>
          <ReceiptListItemStyled>Amount Paid</ReceiptListItemStyled>
          <ReceiptListItemTextStyled>
            <Money amount={amountPaid ?? 0} />
          </ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      )}

      {showBalance && (
        <ReceiptListRowStyled data-tid-receipt-balance=''>
          <ReceiptListItemStyled>Balance</ReceiptListItemStyled>
          <ReceiptListItemTextStyled>
            <Money amount={accountBalance ?? 0} />
          </ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      )}

      <ReceiptListRowStyled data-tid-receipt-ticket=''>
        <ReceiptListItemStyled>Ticket</ReceiptListItemStyled>
        <ReceiptListItemTextStyled>{ticketNumber}</ReceiptListItemTextStyled>
      </ReceiptListRowStyled>

      {specialOffers
        ? specialOffers.map((special, i) => {
            return (
              <ReceiptListRowStyled key={i} data-tid-receipt-special=''>
                <ReceiptListItemStyled>{special.title}</ReceiptListItemStyled>
                <ReceiptListItemTextStyled>
                  <Icon type='specials' size='1.6rem' color={hexColors.orange} />
                </ReceiptListItemTextStyled>
              </ReceiptListRowStyled>
            )
          })
        : null}
    </ReceiptListStyled>
  )
}
